import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Cpu } from 'lucide-react';
import { useState } from 'react';
import {
  listInferenceWorkers,
  type InferenceWorker,
  type InferenceWorkerSetupReason
} from '@/api/fleet';

/**
 * Human-readable mapping for setupStatus reason codes. Strings come from
 * the desktop InferenceWorker's self-report — server-side they're already
 * filtered against a whitelist, so anything we see here is a known code.
 * If the server adds a new reason, add the mapping here too.
 */
const REASON_LABEL: Record<InferenceWorkerSetupReason, string> = {
  ollama_not_running: 'Ollama daemon not reachable',
  ollama_version_too_old: 'Ollama version too old',
  no_models_pulled: 'No inference models pulled to disk yet',
  gpu_not_detected: 'No GPU detected',
  gpu_driver_mismatch: 'GPU driver / CUDA version mismatch',
  insufficient_vram: 'Not enough VRAM for any supported model',
  port_in_use: 'Inference port already taken on this host',
  config_missing: 'Worker config file missing'
};

const REASON_FIX: Partial<Record<InferenceWorkerSetupReason, string>> = {
  ollama_not_running: 'Start Ollama, then the worker auto-recovers on next heartbeat.',
  ollama_version_too_old: 'Upgrade Ollama to the latest stable.',
  no_models_pulled: 'Run `ollama pull` for at least one supported model — the worker picks them up automatically.',
  gpu_not_detected: 'Confirm the host actually has a GPU and the kernel sees it. CPU-only inference is not supported for edge serving.',
  gpu_driver_mismatch: 'Reinstall the GPU driver matching the CUDA/ROCm runtime the worker ships with.',
  insufficient_vram: "Worker won't load a model that doesn't fit. Either upgrade the GPU or restrict the model catalog.",
  port_in_use: 'Free the conflicting port or change the worker config to a different one.',
  config_missing: "Reinstall the desktop agent — config files didn't write."
};

const PLATFORM_EMOJI: Record<string, string> = {
  linux: '🐧',
  windows: '🪟',
  mac: '🍎',
  unknown: '·'
};

function WorkerRow({ w }: { w: InferenceWorker }) {
  const notReady = !w.setupStatus.ready;
  return (
    <li
      className={`rounded-lg border p-3 ${
        notReady
          ? 'border-amber-500/30 bg-amber-500/5'
          : 'border-nurev-border bg-white/[0.02]'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-base leading-none">
              {PLATFORM_EMOJI[w.platform] ?? '·'}
            </span>
            <code className="font-mono text-xs text-nurev-muted">
              {w.workerId.slice(0, 16)}
            </code>
            {notReady ? (
              <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                Setup incomplete
              </span>
            ) : (
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300">
                Ready
              </span>
            )}
          </div>
          {w.gpu.model && (
            <p className="mt-0.5 text-xs text-nurev-muted">
              {w.gpu.vendor} {w.gpu.model}
              {w.gpu.vramMb ? ` · ${(w.gpu.vramMb / 1024).toFixed(0)} GB VRAM` : ''}
              {w.ollamaVersion ? ` · Ollama ${w.ollamaVersion}` : ''}
            </p>
          )}
          {w.supportedModels.length > 0 && (
            <p className="mt-0.5 font-mono text-[10px] text-nurev-muted">
              {w.supportedModels.join(', ')}
            </p>
          )}
        </div>
        <div className="text-right text-[10px] text-nurev-muted">
          {w.activeJobs} active · {w.uptimeSec ? `${Math.floor(w.uptimeSec / 60)}m` : '—'} up
        </div>
      </div>
      {notReady && w.setupStatus.reasons.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-amber-500/15 pt-2">
          {w.setupStatus.reasons.map(r => (
            <li key={r} className="text-xs">
              <span className="font-medium text-amber-200">
                {REASON_LABEL[r] ?? r}
              </span>
              {REASON_FIX[r] && (
                <span className="ml-1 text-nurev-text-soft">
                  — {REASON_FIX[r]}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function InferenceWorkersBadge() {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['fleet-inference-workers'],
    queryFn: listInferenceWorkers,
    refetchInterval: 30_000
  });

  if (isLoading || !data) {
    return null;
  }
  const { workers, summary } = data;
  if (summary.total === 0) {
    // No inference workers connected at all — don't surface anything.
    // The operator hasn't opted into inference workloads; an empty
    // badge would just be noise.
    return null;
  }

  const notReady = summary.notReady > 0;

  return (
    <section
      className={`rounded-xl border ${
        notReady
          ? 'border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-amber-500/[0.02]'
          : 'border-emerald-500/25 bg-gradient-to-br from-emerald-500/5 to-emerald-500/[0.02]'
      } p-4 lg:p-5`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div
            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
              notReady
                ? 'bg-amber-500/15 text-amber-300'
                : 'bg-emerald-500/15 text-emerald-300'
            }`}
          >
            {notReady ? (
              <AlertTriangle className="h-5 w-5" />
            ) : (
              <CheckCircle2 className="h-5 w-5" />
            )}
          </div>
          <div>
            <h2 className="text-sm font-semibold text-nurev-text">
              {notReady
                ? `${summary.notReady} of ${summary.total} inference workers need setup attention`
                : `All ${summary.total} inference workers ready`}
            </h2>
            <p className="mt-0.5 text-xs text-nurev-text-soft">
              {notReady ? (
                <>
                  These workers are online but not serving traffic. Each
                  reason below has a one-line fix; tackle one and the worker
                  reports back on its next heartbeat (~10 sec).
                </>
              ) : (
                <>
                  Serving normally. Workers report self-reported setup
                  state every ~10 seconds.
                </>
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text"
        >
          <Cpu className="h-3.5 w-3.5" />
          {expanded ? 'Hide' : 'Show'} {summary.total} worker{summary.total === 1 ? '' : 's'}
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
      {expanded && (
        <ul className="mt-4 space-y-2">
          {workers.map(w => (
            <WorkerRow key={w.workerId} w={w} />
          ))}
        </ul>
      )}
    </section>
  );
}
